import { useCallback } from 'react'
import { graphql, useFragment, useMutation } from 'react-relay'

import { type useProfile_query$data, type useProfile_query$key } from '@/__generated__/useProfile_query.graphql'
import { type ProfilePatch, type useProfileMutation } from '@/__generated__/useProfileMutation.graphql'

type CommitAsyncFn = (patch: ProfilePatch) => Promise<useProfileMutation['response']>

export const useProfile = (
  queryRef?: useProfile_query$key | null | undefined
): [NonEmpty<useProfile_query$data['currentUser']>['profile'], CommitAsyncFn, boolean] => {
  const queryData = useFragment(
    graphql`
      fragment useProfile_query on Query {
        currentUser {
          profile {
            beliefSystemId
            biography
            childrenPreferenceId
            closestCity
            educationLevelId
            genderId
            genderOther
            heightCm
            heightFt
            heightIn
            instagram
            occupation
            politicalViewId
            pronounId
            rowId
            school
            vaccinationStatusId
            ...OwnProfileInfo_profile
          }
        }
      }
    `,
    queryRef
  )
  const [commit, isPending] = useMutation<useProfileMutation>(graphql`
    mutation useProfileMutation($input: UpdateProfileInput!) {
      updateProfile(input: $input) {
        query {
          ...useProfile_query
        }
      }
    }
  `)

  const commitAsync = useCallback<CommitAsyncFn>(
    (patch: ProfilePatch) =>
      new Promise((resolve, reject) =>
        commit({
          onCompleted: (response, errors) => {
            if (errors) {
              reject(errors)
            } else {
              resolve(response)
            }
          },
          variables: {
            input: {
              rowId: queryData?.currentUser?.profile?.rowId,
              patch
            }
          }
        })
      ),
    [commit, queryData?.currentUser?.profile?.rowId]
  )

  return [queryData?.currentUser?.profile, commitAsync, isPending]
}
